import { useToastController } from "@tamagui/toast";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { YStack } from "tamagui";

import {
  useListOutstandingProgressionQuestionnaires,
  useSubmitProgressionQuestionnaireResponse,
} from "@/api/generated";
import {
  type QuestionnaireDefinition as ApiQuestionnaireDefinition,
  QuestionControl,
} from "@/api/generated/models";
import { Button, ConstrainedView, Footer } from "@/components";
import { Heading, Link, Paragraph } from "@/components/Typography";
import QuestionnaireRenderer, {
  type QuestionnaireAnswers,
} from "@/conversation/QuestionnaireRenderer";
import type { QuestionnaireDefinition } from "@/conversation/QuestionnaireTypes";
import PendingContent from "@/conversation/sidebar/UserProgression/PendingContent";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";

const Phq9PositiveCompletion = () => (
  <YStack flex={1} justifyContent="flex-start" gap="$300">
    <Heading size="$lg">Thank you for sharing with us.</Heading>
    <Paragraph color="$textPrimary" bold>
      If you believe you are experiencing a medical or psychiatric emergency,
      call{" "}
      <Link bold href="tel:911">
        911
      </Link>{" "}
      or go to the nearest emergency room immediately.
    </Paragraph>
    <Paragraph color="$textPrimary">
      You can also call or text <Link href="tel:988">988</Link> (Suicide &amp;
      Crisis Lifeline) any time to talk with someone right away.
    </Paragraph>
    <Paragraph color="$textPrimary">
      If you&apos;d still like to continue, you can talk with one of our
      clinicians.
    </Paragraph>
  </YStack>
);

const QuestionnaireCompletion = () => (
  <YStack flex={1} justifyContent="flex-start" gap="$300">
    <Heading size="$lg">Thank you for sharing with us.</Heading>
    <Paragraph color="$textPrimary">
      Your questionnaire answers will be reviewed by your clinician during your
      visit.
    </Paragraph>
  </YStack>
);

const isPhq9Q9Positive = (answers: QuestionnaireAnswers) => {
  const q9Answer = answers["curai-phq-9"]?.q9;
  return !!q9Answer && ["1", "2", "3"].includes(q9Answer);
};

const QUESTIONNAIRE_TITLES: Partial<Record<string, string>> = {
  "curai-gad-7": "GAD-7 (Generalized Anxiety Disorder-7)",
  "curai-phq-9": "PHQ-9 (Patient Health Questionnaire-9)",
};

const toRendererDefinition = (
  definition: ApiQuestionnaireDefinition,
): QuestionnaireDefinition | null => {
  if (
    definition.stages.length !== 1 ||
    definition.stages[0].transition.type !== "complete"
  ) {
    return null;
  }

  const questions = definition.stages[0].questions;
  if (
    questions.some(
      (question) =>
        question.control !== QuestionControl.single_choice ||
        !question.options?.length,
    )
  ) {
    return null;
  }

  return {
    id: definition.id,
    title: QUESTIONNAIRE_TITLES[definition.id] ?? definition.title,
    completionMessage: definition.completion_message ?? undefined,
    questions: questions.map((question) => ({
      id: question.id,
      text: question.text,
      required: question.required ?? false,
      options: (question.options ?? []).map(({ value, label }) => ({
        value,
        label,
      })),
    })),
  };
};

type QuestionnaireProgressionStepProps = {
  progressionId: string;
  onContinue: () => Promise<boolean>;
  onClose: () => void;
  isPending: boolean;
  onBackButtonChange?: (show: boolean, onPress?: (() => void) | null) => void;
};

const QuestionnaireProgressionStep = ({
  progressionId,
  onContinue,
  onClose,
  isPending,
  onBackButtonChange,
}: QuestionnaireProgressionStepProps) => {
  const progression = useUserProgression()?.progression;
  const hasPersistedPositiveAnswer =
    progression?.metadata?.phq9_q9_positive === true;
  const [hasSubmittedPositiveAnswer, setHasSubmittedPositiveAnswer] =
    useState(false);
  const showSelfHarmFollowUp =
    hasPersistedPositiveAnswer || hasSubmittedPositiveAnswer;
  const continueLabel = showSelfHarmFollowUp
    ? "Talk to clinician"
    : "Continue enrollment";
  const { show: showToast } = useToastController();
  const [isContinuing, setIsContinuing] = useState(false);
  const [frozenDefinitions, setFrozenDefinitions] = useState<
    ApiQuestionnaireDefinition[] | null
  >(null);
  const {
    data: definitions,
    isLoading,
    isError,
    refetch,
  } = useListOutstandingProgressionQuestionnaires(progressionId, {
    query: { meta: { suppressErrorToast: true } },
  });
  const { mutateAsync: submitResponse } =
    useSubmitProgressionQuestionnaireResponse({
      mutation: { meta: { suppressErrorToast: true } },
    });
  const activeDefinitions = frozenDefinitions ?? definitions;

  useEffect(() => {
    const hasNoOutstandingQuestionnaires =
      !isLoading && !isError && !activeDefinitions?.length;
    onBackButtonChange?.(
      false,
      hasNoOutstandingQuestionnaires ? null : undefined,
    );
  }, [activeDefinitions?.length, isError, isLoading, onBackButtonChange]);

  useEffect(() => () => onBackButtonChange?.(false), [onBackButtonChange]);

  const handleContinue = useCallback(async () => {
    setIsContinuing(true);
    const advanced = await onContinue();
    setIsContinuing(false);
    return advanced;
  }, [onContinue]);

  const refreshAfterRejectedSubmission = useCallback(
    async (error: unknown) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status !== 409 && status !== 422) return;
      const refreshed = await refetch().catch(() => null);
      if (refreshed?.isSuccess) setFrozenDefinitions(null);
    },
    [refetch],
  );

  const handleSubmit = useCallback(
    async (answers: QuestionnaireAnswers) => {
      if (!activeDefinitions) return false;
      setFrozenDefinitions(activeDefinitions);

      try {
        for (const definition of activeDefinitions) {
          await submitResponse({
            progressionId,
            questionnaireId: definition.id,
            data: {
              checksum: definition.checksum,
              answers: answers[definition.id] ?? {},
            },
          });
        }
        setHasSubmittedPositiveAnswer(isPhq9Q9Positive(answers));
      } catch (error) {
        await refreshAfterRejectedSubmission(error);
        showToast("Something went wrong. Please try again.");
        return false;
      }

      return true;
    },
    [
      activeDefinitions,
      progressionId,
      refreshAfterRejectedSubmission,
      showToast,
      submitResponse,
    ],
  );

  if (isLoading && !activeDefinitions) return <PendingContent />;

  if (isError && !activeDefinitions) {
    return (
      <PendingContent
        message={{
          title: "We couldn't load your questionnaires",
          subtitle: "Please try again.",
        }}
        ctaLabel="Try again"
        onCtaPress={() => {
          void refetch();
        }}
        showSupportLink
      />
    );
  }

  if (!activeDefinitions?.length) {
    if (hasPersistedPositiveAnswer) {
      return (
        <ConstrainedView flex={1} paddingVertical="$300">
          <YStack flex={1}>
            <Phq9PositiveCompletion />
          </YStack>
          <Footer mode="dialog">
            <Button
              mode="primary"
              constrained
              onPress={() => void handleContinue()}
              disabled={isPending || isContinuing}
              loading={isPending || isContinuing}
              dataTrackingId="questionnaire-continue-empty"
            >
              {continueLabel}
            </Button>
          </Footer>
        </ConstrainedView>
      );
    }

    return (
      <ConstrainedView flex={1} paddingVertical="$300">
        <PendingContent
          message={{
            title: "No questionnaires to complete",
            subtitle: "You can continue with your enrollment.",
          }}
        />
        <Footer mode="dialog">
          <Button
            mode="primary"
            constrained
            onPress={() => void handleContinue()}
            disabled={isPending || isContinuing}
            loading={isPending || isContinuing}
            dataTrackingId="questionnaire-continue-empty"
          >
            Continue enrollment
          </Button>
        </Footer>
      </ConstrainedView>
    );
  }

  const rendererDefinitions = activeDefinitions.map(toRendererDefinition);
  if (rendererDefinitions.some((definition) => definition === null)) {
    return (
      <PendingContent
        message={{
          title: "This questionnaire can't be displayed right now",
          subtitle: "Please try again.",
        }}
        ctaLabel="Try again"
        onCtaPress={() => {
          void refetch();
        }}
        showSupportLink
      />
    );
  }

  return (
    <QuestionnaireRenderer
      key={activeDefinitions
        .map(({ id, checksum }) => `${id}:${checksum}`)
        .join("|")}
      definitions={rendererDefinitions as QuestionnaireDefinition[]}
      onSubmit={handleSubmit}
      onComplete={handleContinue}
      completionButtonLabel={continueLabel}
      onClose={onClose}
      showHeader={false}
      onBackButtonChange={onBackButtonChange}
      renderCompletionContent={() =>
        showSelfHarmFollowUp ? (
          <Phq9PositiveCompletion />
        ) : (
          <QuestionnaireCompletion />
        )
      }
    />
  );
};

export default QuestionnaireProgressionStep;
